// Page 편집용 EditorJS 컨트롤러
// editor_core.js의 공통 로직을 사용하며, Notes와 무관한 심플한 구조
//
// Notes editor (components--editor-js)와의 차이:
//   - title/subtitle/tagList 등 Notes 전용 타겟 없음
//   - publishNote, preview 등 Notes 전용 기능 없음
//   - AI skills 연동 없음
//   - auto-save는 editor_data만 PATCH
//
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import { buildTools, createEditorInstance } from "@/editors/editor_core";
import { CollabSession } from '@/editorjs/collab';
import { AwarenessCursor } from '@/editorjs/collab/awareness_cursor';
import { PresencePanel } from '@/editorjs/collab/presence_panel';

export default class extends Controller {
  static targets = ["editorForm", "editorData", "autoSavedLabel", "overlay", "presencePanel"];
  static values = {
    primaryBackgroundColor: String,
    primaryTextColor: String,
    lightMode: Boolean,
    currentPlan: String,
  };

  initialize() {
    this.editor = null;
    this.autoSaver = null;
    this.alreadySaving = false;
    this.isDragging = false;
    this.isTyping = false;
    this.typingTimer = null;
    this.dragResetTimer = null;
    this.sessionExpired = false;

    this.handleKeyDown = this._handleKeyDown.bind(this);
    this.handleSessionExpiredBound = (e) => {
      this.sessionExpired = true;
      // Collab takeover UX: overlay로 사유 안내. overlay target이 있는 경우에만.
      if (e?.detail?.reason === 'collab_takeover' && this.hasOverlayTarget) {
        this.overlayTarget.innerHTML =
          "다른 기기 또는 탭에서 편집을 시작했습니다.<br>여기에서 이어서 편집하려면 페이지를 새로고침해주세요.";
        this.overlayTarget.style.display = 'block';
      }
    };
  }

  async connect() {
    // 이전 인스턴스 정리 (Turbo에 의한 중복 connect 방지)
    if (this.editor) {
      try { this.editor.destroy(); } catch (e) { /* ignore */ }
      this.editor = null;
    }
    clearInterval(this.autoSaver);

    const holder = this.data.get("editor-container") || "pageContainer";

    let defaultData = {};

    try {
      const response = await api.get(this.data.get("fetch-url"));
      defaultData = response.data.editor_data || { blocks: [] };
    } catch (error) {
      console.error("[PageEditor] fetch failed:", error, "url:", this.data.get("fetch-url"));
      alert("콘텐츠를 불러오는 데 실패했습니다. 잠시 후 페이지를 새로고침 해주세요.");
      return;
    }

    const { tools, defaultBlockTunes } = buildTools({
      controller: this,
      lightMode: this.lightModeValue,
      includeAdvanced: true,
      excludeVote: true,
      excludeAiPolish: true,
      includePageWidgets: true,
      spaceUrl: this.data.get("space-url"),
      postExtId: this.data.get("page-id"),
      currentPlan: this.currentPlanValue,
      primaryTextColor: this.primaryTextColorValue,
      primaryBackgroundColor: this.primaryBackgroundColorValue,
    });

    const isCollabMode = this.data.get("collab-mode") === "true";

    this.editor = createEditorInstance({
      holder,
      data: defaultData,
      tools,
      tunes: defaultBlockTunes,
      lightMode: this.lightModeValue,
      collabMode: isCollabMode,
      onReady: ({ undo, holderElement }) => {
        this.undo = undo;
        this._setupDragListeners(holderElement);
        document.addEventListener('session:expired', this.handleSessionExpiredBound);

        if (isCollabMode) {
          this._initCollab(holderElement);
        }
      },
    });

    if (!isCollabMode) {
      const AUTO_SAVE_INTERVAL_MS = 15_000;
      const AUTO_SAVE_DISABLED_MS = 10_000_000;
      const period = process.env.NODE_ENV === 'production' ? AUTO_SAVE_INTERVAL_MS : AUTO_SAVE_DISABLED_MS;
      this.autoSaver = setInterval(() => {
        if (document.hidden) return;
        this.autoSave();
      }, period);
    }

    document.addEventListener("keydown", this.handleKeyDown);
  }

  _initCollab(holderElement) {
    const recordId = parseInt(this.data.get("collab-record-id"), 10);
    const editorId = this.data.get("collab-editor-id") || null;
    const userName = this.data.get("collab-user-name") || "Anonymous";
    const userImage = this.data.get("collab-user-image") || null;

    const presenceContainer = this.hasPresencePanelTarget
        ? this.presencePanelTarget
        : holderElement.parentElement;
    this.presencePanel = new PresencePanel({ container: presenceContainer });

    this.collabSession = new CollabSession({
      editor: this.editor,
      recordType: "Page",
      recordId,
      editorId,
      userName,
      userImage,
      onSynced: () => {
        this.awarenessCursor = new AwarenessCursor({
          awareness: this.collabSession.awareness,
          doc: this.collabSession.doc,
          editor: this.editor,
          holderElement,
        });
      },
      onStatus: (status) => this.presencePanel?.setStatus(status),
      onPresence: (data) => this.presencePanel?.handlePresenceEvent(data),
    });

    this.collabSession.connect();

    this._collabKeydownHandler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        e.stopPropagation();
        e.shiftKey ? this.collabSession.redo() : this.collabSession.undo();
      }
    };
    holderElement.addEventListener('keydown', this._collabKeydownHandler, true);
  }

  disconnect() {
    clearInterval(this.autoSaver);
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener('session:expired', this.handleSessionExpiredBound);

    if (this.collabSession) { this.collabSession.destroy(); this.collabSession = null; }
    if (this.awarenessCursor) { this.awarenessCursor.destroy(); this.awarenessCursor = null; }
    if (this.presencePanel) { this.presencePanel.destroy(); this.presencePanel = null; }

    if (this.editor && typeof this.editor.destroy === 'function') {
      this.editor.destroy();
      this.editor = null;
    }
  }

  // --- Actions ---

  submitForm() {
    if (this.sessionExpired) {
      alert("세션이 만료되어 저장할 수 없습니다.");
      return;
    }
    clearInterval(this.autoSaver);
    this.data.set("protect-leave", "off");

    this.editor.save().then((outputData) => {
      this.editorDataTarget.value = JSON.stringify(outputData);
      this.editorFormTarget.submit();
    }).catch((error) => {
      console.error("[PageEditor] save failed:", error);
    });
  }

  autoSave() {
    if (this.sessionExpired || this.isDragging || this.alreadySaving || this.isTyping) return;
    if (!this.editor) return;

    this.alreadySaving = true;
    this.editor.save().then((outputData) => {
      if (process.env.NODE_ENV === "development") {
        console.log("[PageEditor] autoSave blocks:", outputData.blocks);
      }

      // 빈 콘텐츠로 기존 페이지를 덮어쓰는 걸 방지
      const isEmpty = !outputData.blocks || outputData.blocks.length === 0;
      if (isEmpty && this.data.get("page-id") !== "new") {
        this.alreadySaving = false;
        return;
      }

      this.editorDataTarget.value = JSON.stringify(outputData);

      const params = {
        commit: "auto_save",
        [this.data.get("param-key")]: {
          editor_data: JSON.stringify(outputData)
        }
      };

      api.patch(this.data.get("update-url"), params)
        .then((res) => {
          const msg = res?.data?.succeed_message;
          if (this.hasAutoSavedLabelTarget && msg) {
            this.autoSavedLabelTarget.classList.remove("hidden");
            this.autoSavedLabelTarget.innerHTML = `${new Date().toLocaleTimeString()}<br>${msg}`;
          }
        })
        .catch((error) => {
          console.error("[PageEditor] autoSave API failed:", error);
        })
        .finally(() => {
          this.alreadySaving = false;
        });
    }).catch(() => {
      this.alreadySaving = false;
    });
  }

  leavingPage = (event) => {
    const areYouSure = "정말 페이지를 나가시나요? 저장을 하셨는지 꼭 확인해주세요 :)";
    if ((this.data.get("protect-leave") === "on") && (process.env.NODE_ENV === "production")) {
      if (event.type === "turbo:before-visit") {
        if (!confirm(areYouSure)) {
          event.preventDefault();
        }
      } else {
        event.returnValue = areYouSure;
        return areYouSure;
      }
    }
  };

  // --- Private ---

  _setupDragListeners(holderElement) {
    const resetDragState = () => {
      clearTimeout(this.dragResetTimer);
      this.dragResetTimer = setTimeout(() => { this.isDragging = false; }, 500);
    };

    holderElement.addEventListener('dragstart', () => {
      this.isDragging = true;
      clearTimeout(this.dragResetTimer);
      this.dragResetTimer = setTimeout(() => { this.isDragging = false; }, 5000);
    });
    holderElement.addEventListener('drop', resetDragState);
    holderElement.addEventListener('dragend', resetDragState);

    holderElement.addEventListener('keydown', (e) => {
      if (this.isDragging) {
        this.isDragging = false;
        clearTimeout(this.dragResetTimer);
      }
      if (['Enter', 'Delete', 'Backspace'].includes(e.key)) {
        this.isTyping = true;
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(() => { this.isTyping = false; }, 500);
      }
    });
  }

  _handleKeyDown(e) {
    // CMD+S / Ctrl+S → 저장
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      this.autoSave();
    }
  }
}
